import { AuthenticationStore } from "@istari-frontend/auth";
import { V3Models } from "@istari/istari-client";

import { fromChatMessage } from "~/ai-chat/data-source/adapter/v3/from-chat-message";
import type { ChatMessage } from "~/ai-chat/model/chat-message";
import { resolveRegistryBaseUrl } from "~/shared/config/gateway-urls";

/**
 * A single event from the streaming chat endpoint (`POST /api/v3/ai/chat/stream`).
 *
 * The backend emits Server-Sent Events; this is the decoded, camel-cased shape
 * the UI consumes. Mirrors the backend contract in
 * `fileservice2/v3/ai/ai_dto.py` (StreamEventType + payloads):
 *   - delta      -> incremental assistant text
 *   - tool_call  -> a tool started/finished (name only, no args/results)
 *   - done       -> terminal success, carries mcp_server_url
 *   - error      -> terminal failure (safe message)
 */
export type ChatStreamEvent =
  | { type: "delta"; content: string }
  | { type: "toolCall"; name: string; status: "started" | "finished" }
  | { type: "done"; mcpServerUrl: string | null }
  | { type: "error"; code: string; message: string };

export interface StreamChatMessageParams {
  messages: ChatMessage[];
  systemPrompt?: string | null;
  signal?: AbortSignal;
  onEvent: (event: ChatStreamEvent) => void;
}

/**
 * Open the streaming chat endpoint and invoke `onEvent` for each decoded event.
 *
 * Resolves when the stream ends (a `done` or `error` event, or the connection
 * closing). Throws before any event if the request fails to open; the thrown
 * value carries `{ response }` so callers can reuse `extractBackendError`
 * (matching the SDK's `ResponseError` shape) to preserve the 404 handling.
 *
 * Uses a hand-rolled `fetch` + `ReadableStream` reader rather than the generated
 * SDK (which buffers/parses the whole body and cannot stream) or
 * `EventSource` (GET-only, cannot send the `Authorization` header).
 */
export async function streamChatMessage({
  messages,
  systemPrompt,
  signal,
  onEvent,
}: StreamChatMessageParams): Promise<void> {
  const token = AuthenticationStore().getAccessToken() || "";
  const response = await fetch(`${resolveRegistryBaseUrl()}/api/v3/ai/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // Serialize through the SDK's ChatRequestToJSON so the wire body matches
    // every other V3 request (camelCase -> snake_case, e.g. system_prompt).
    // Hand-building the JSON here previously dropped the page context because
    // the backend expects `system_prompt`, not `systemPrompt`.
    body: JSON.stringify(
      V3Models.ChatRequestToJSON({
        messages: messages.map(fromChatMessage),
        systemPrompt: systemPrompt ?? undefined,
      }),
    ),
    signal,
  });

  if (!response.ok || response.body === null) {
    // Shape the error like the SDK's ResponseError so useChatSend's
    // extractBackendError (which duck-types `error.response instanceof Response`)
    // still routes 404 -> "not configured".
    throw Object.assign(new Error(`Chat stream failed with status ${response.status}`), { response });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // Normalize line endings so frames split cleanly on a blank line.
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = parseFrame(frame);
        if (event) onEvent(event);
        boundary = buffer.indexOf("\n\n");
      }
    }
    // Flush any bytes held back by the streaming decoder, then parse a trailing
    // frame that wasn't terminated by a blank line.
    buffer += decoder.decode().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const tail = parseFrame(buffer);
    if (tail) onEvent(tail);
  } finally {
    reader.releaseLock();
  }
}

// Parse one SSE frame. Comment lines (`:` — used by the 15s keep-alive ping)
// and blank lines are ignored; multiple `data:` lines concatenate per the spec.
function parseFrame(raw: string): ChatStreamEvent | null {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }
  if (dataLines.length === 0) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return null;
  }
  return toStreamEvent(eventName, payload);
}

function toStreamEvent(name: string, data: unknown): ChatStreamEvent | null {
  const obj = (data !== null && typeof data === "object" ? data : {}) as Record<string, unknown>;
  switch (name) {
    case "delta":
      return typeof obj.content === "string" ? { type: "delta", content: obj.content } : null;
    case "tool_call":
      return typeof obj.name === "string" && (obj.status === "started" || obj.status === "finished")
        ? { type: "toolCall", name: obj.name, status: obj.status }
        : null;
    case "done":
      return { type: "done", mcpServerUrl: typeof obj.mcp_server_url === "string" ? obj.mcp_server_url : null };
    case "error":
      return {
        type: "error",
        code: typeof obj.code === "string" ? obj.code : "unknown",
        message: typeof obj.message === "string" ? obj.message : "The assistant ran into a problem.",
      };
    default:
      return null;
  }
}
